// src/components/AdminPanel.jsx
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AdminMap from './AdminMap'

/**
 * Admin panel for setting up treasures and hint
 * @param {Function} onSave - Callback with hunt data to save
 * @param {Object} initialHunt - Hunt data to populate form (optional)
 */
export default function AdminPanel({ onSave, initialHunt }) {
  const { t } = useTranslation()
  const [hunt, setHunt] = useState(null)
  const [huntType, setHuntType] = useState('easter') // 'easter' or 'birthday'
  const [unlockMode, setUnlockMode] = useState('keyword') // 'keyword' or 'proximity'
  const [treasures, setTreasures] = useState([])
  const [newTreasure, setNewTreasure] = useState({ lat: '', lng: '', keyword: '' })
  const [editingIndex, setEditingIndex] = useState(null)
  const [finalHint, setFinalHint] = useState('')
  const [finalWords, setFinalWords] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  // Load existing hunt on mount
  useEffect(() => {
    if (initialHunt) {
      setHunt(initialHunt)
      setHuntType(initialHunt.huntType || 'easter')
      setUnlockMode(initialHunt.unlockMode || 'keyword')
      setTreasures(initialHunt.treasures || [])
      setFinalHint(initialHunt.finalHint || '')
      setFinalWords(initialHunt.finalWords && Array.isArray(initialHunt.finalWords) ? initialHunt.finalWords.join(' ') : '')
    }
  }, [initialHunt])

  function handleAddTreasure() {
    // In proximity mode there is no keyword to fill in.
    const keywordMissing = unlockMode === 'keyword' && !newTreasure.keyword
    if (!newTreasure.lat || !newTreasure.lng || keywordMissing) {
      setMessage({ type: 'error', text: t('admin.errorFillAllFields') })
      return
    }

    const treasure = {
      id: `t${Date.now()}`,
      lat: parseFloat(newTreasure.lat),
      lng: parseFloat(newTreasure.lng),
      keyword: unlockMode === 'proximity' ? null : newTreasure.keyword,
      found: false
    }

    let updated
    if (editingIndex !== null) {
      // Replace treasure at its original position
      updated = [...treasures]
      updated[editingIndex] = treasure
      setEditingIndex(null)
      setMessage({ type: 'success', text: t('admin.treasureUpdated') })
    } else {
      // Add new treasure
      updated = [...treasures, treasure]
      setMessage({ type: 'success', text: t('admin.treasureAdded') })
    }

    setTreasures(updated)
    setNewTreasure({ lat: '', lng: '', keyword: '' })

    // Auto-generate final words from keywords (proximity hunts have none)
    if (unlockMode === 'keyword') {
      setFinalWords(updated.map(t => t.keyword).join(' '))
    }

    setTimeout(() => setMessage(null), 3000)
  }

  function handleSetCoords(lat, lng) {
    setNewTreasure(prev => ({ ...prev, lat: lat.toFixed(5), lng: lng.toFixed(5) }))
  }

  function handleEditTreasure(treasure, index) {
    setNewTreasure({ lat: String(treasure.lat), lng: String(treasure.lng), keyword: treasure.keyword })
    setEditingIndex(index)
  }

  function handleRemoveTreasure(id) {
    const updated = treasures.filter(t => t.id !== id)
    setTreasures(updated)
    if (editingIndex !== null) {
      setEditingIndex(null)
      setNewTreasure({ lat: '', lng: '', keyword: '' })
    }

    // Auto-generate final words from remaining keywords (proximity has none)
    if (unlockMode === 'keyword') {
      setFinalWords(updated.map(t => t.keyword).join(' '))
    }
  }

  function handleReorderTreasure(index, direction) {
    const updated = [...treasures]
    if (direction === 'up' && index > 0) {
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]]
    } else if (direction === 'down' && index < updated.length - 1) {
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]]
    }
    setTreasures(updated)
  }

  function handleReorderWord(index, direction) {
    const words = finalWords.trim().split(/\s+/).filter(w => w.length > 0)
    if (direction === 'up' && index > 0) {
      [words[index - 1], words[index]] = [words[index], words[index - 1]]
    } else if (direction === 'down' && index < words.length - 1) {
      [words[index], words[index + 1]] = [words[index + 1], words[index]]
    }
    setFinalWords(words.join(' '))
  }

  function handleSave() {
    if (treasures.length === 0) {
      setMessage({ type: 'error', text: t('admin.errorNoTreasures') })
      return
    }

    // Proximity hunts have no final word puzzle, so there is nothing to match.
    const words = unlockMode === 'proximity'
      ? []
      : finalWords.trim().split(/\s+/).filter(w => w.length > 0)

    if (unlockMode === 'keyword' && words.length !== treasures.length) {
      setMessage({ type: 'error', text: t('admin.errorWordCountMismatch', { count: treasures.length, plural: treasures.length !== 1 ? 's' : '' }) })
      return
    }

    setSaving(true)

    // Drop keywords a hunt may have carried over from a previous keyword setup,
    // so the saved data matches the mode it is saved in.
    const savedTreasures = unlockMode === 'proximity'
      ? treasures.map(treasure => ({ ...treasure, keyword: null }))
      : treasures

    const huntData = {
      id: hunt?.id || `hunt-${Date.now()}`,
      name: hunt?.name || 'Treasure Hunt',
      huntType,
      unlockMode,
      treasures: savedTreasures,
      finalHint,
      finalWords: words,
      completed: hunt?.completed || false,
      completedAt: hunt?.completedAt || null
    }

    // Call parent handler to save
    onSave(huntData)

    setSaving(false)
    setMessage({ type: 'success', text: t('admin.huntSaved') })

    setTimeout(() => setMessage(null), 3000)
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{t('admin.title')}</h1>

      {message && (
        <div style={{
          ...styles.message,
          background: message.type === 'error' ? '#f8d7da' : '#d4edda',
          color: message.type === 'error' ? '#721c24' : '#155724',
          border: `1px solid ${message.type === 'error' ? '#f5c6cb' : '#c3e6cb'}`
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.mainLayout}>
        {/* Left Column: Form and Treasures */}
        <div style={styles.leftColumn}>
          {/* Hunt Type Selector */}
          <section style={styles.section}>
            <h2>Hunt Type</h2>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <button
                onClick={() => setHuntType('easter')}
                style={{
                  ...styles.button,
                  background: huntType === 'easter' ? '#28a745' : '#e0e0e0',
                  color: huntType === 'easter' ? 'white' : '#333'
                }}
              >
                🥚 Easter Hunt
              </button>
              <button
                onClick={() => setHuntType('birthday')}
                style={{
                  ...styles.button,
                  background: huntType === 'birthday' ? '#28a745' : '#e0e0e0',
                  color: huntType === 'birthday' ? 'white' : '#333'
                }}
              >
                🎁 Birthday Hunt
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
              {huntType === 'easter' ? 'Egg 🥚 and Chicken 🐥' : 'Present 🎁 and Surprise 🎉'}
            </p>
          </section>

          {/* Unlock Mode Selector */}
          <section style={styles.section}>
            <h2>Unlock Mode</h2>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <button
                onClick={() => setUnlockMode('keyword')}
                style={{
                  ...styles.button,
                  background: unlockMode === 'keyword' ? '#28a745' : '#e0e0e0',
                  color: unlockMode === 'keyword' ? 'white' : '#333'
                }}
              >
                🔑 Keyword
              </button>
              <button
                onClick={() => setUnlockMode('proximity')}
                style={{
                  ...styles.button,
                  background: unlockMode === 'proximity' ? '#28a745' : '#e0e0e0',
                  color: unlockMode === 'proximity' ? 'white' : '#333'
                }}
              >
                📍 Proximity (10 m)
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
              {unlockMode === 'keyword'
                ? 'Players type the keyword found at each treasure, then solve the final word puzzle.'
                : 'Treasures unlock automatically within 10 m. No keywords, no final word puzzle — the hunt ends with the final hint.'}
            </p>
          </section>

          {/* Add Treasure Form */}
          <section style={styles.section}>
            <h2>{t('admin.addTreasures')}</h2>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '10px' }}>
              {t('admin.clickToEdit')}
            </p>

          <div style={styles.form}>
          <input
            type="number"
            placeholder={t('admin.latitude')}
            value={newTreasure.lat}
            onChange={(e) => setNewTreasure({ ...newTreasure, lat: e.target.value })}
            step="0.0001"
            style={styles.input}
          />
          <input
            type="number"
            placeholder={t('admin.longitude')}
            value={newTreasure.lng}
            onChange={(e) => setNewTreasure({ ...newTreasure, lng: e.target.value })}
            step="0.0001"
            style={styles.input}
          />
          {unlockMode === 'keyword' && (
            <input
              type="text"
              placeholder={t('admin.keywordPlaceholder')}
              value={newTreasure.keyword || ''}
              onChange={(e) => setNewTreasure({ ...newTreasure, keyword: e.target.value })}
              style={styles.input}
            />
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleAddTreasure} style={{ ...styles.button, flex: 1 }}>
              {editingIndex !== null ? t('admin.updateButton') : t('admin.addButton')}
            </button>
            {editingIndex !== null && (
              <button
                onClick={() => {
                  setEditingIndex(null)
                  setNewTreasure({ lat: '', lng: '', keyword: '' })
                }}
                style={{ ...styles.button, background: '#6c757d' }}
              >
                {t('admin.cancelButton')}
              </button>
            )}
          </div>
        </div>

        {/* Treasures List */}
        <div style={styles.treasuresList}>
          {treasures.map((treasure, i) => {
            const isFirst = i === 0
            const isLast = i === treasures.length - 1
            const isEditing = editingIndex === i
            return (
              <div key={treasure.id} style={{
                ...styles.treasureItem,
                background: isEditing ? '#e8f4f8' : 'white',
                borderColor: isEditing ? '#007bff' : '#ddd',
                borderWidth: isEditing ? '2px' : '1px'
              }}>
                <div
                  onClick={() => handleEditTreasure(treasure, i)}
                  style={{ cursor: 'pointer', flex: 1, textDecoration: 'underline', textDecorationColor: '#007bff' }}
                >
                  <strong>{t('admin.treasure', { number: i + 1 })}</strong><br />
                  {t('admin.lat')}: {treasure.lat}, {t('admin.lng')}: {treasure.lng}
                  {unlockMode === 'keyword' && (
                    <><br />{t('admin.keyword')}: <code>{treasure.keyword}</code></>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => handleReorderTreasure(i, 'up')}
                    disabled={isFirst}
                    style={{
                      ...styles.smallButton,
                      opacity: isFirst ? 0.3 : 1,
                      cursor: isFirst ? 'not-allowed' : 'pointer'
                    }}
                    title={t('admin.moveUp')}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => handleReorderTreasure(i, 'down')}
                    disabled={isLast}
                    style={{
                      ...styles.smallButton,
                      opacity: isLast ? 0.3 : 1,
                      cursor: isLast ? 'not-allowed' : 'pointer'
                    }}
                    title={t('admin.moveDown')}
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => handleRemoveTreasure(treasure.id)}
                    style={{ ...styles.button, background: '#dc3545' }}
                  >
                    {t('admin.remove')}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
          {treasures.length === 1 ? t('admin.treasureCount', { count: treasures.length }) : t('admin.treasureCountPlural', { count: treasures.length })}
        </p>
          </section>

          {/* Final Hint Setup */}
          <section style={styles.section}>
            <h2>{unlockMode === 'keyword' ? t('admin.finalHintSentence') : t('admin.finalHintProximity')}</h2>
            <p style={{ color: '#666', marginBottom: '10px' }}>
              {unlockMode === 'keyword'
                ? t('admin.finalHintDescription')
                : t('admin.finalHintDescriptionProximity')}
            </p>
        {unlockMode === 'keyword' && (
        <div style={{
          padding: '12px',
          background: '#f0f0f0',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          {finalWords.trim().length > 0 ? (
            <div>
              <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#666' }}>
                <strong>{t('admin.wordOrder')}</strong>
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {finalWords.trim().split(/\s+/).filter(w => w.length > 0).map((word, idx, arr) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 10px',
                      background: 'white',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  >
                    <span style={{ fontWeight: 'bold', minWidth: '60px' }}>{word}</span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => handleReorderWord(idx, 'up')}
                        disabled={idx === 0}
                        style={{
                          ...styles.smallButton,
                          opacity: idx === 0 ? 0.3 : 1,
                          cursor: idx === 0 ? 'not-allowed' : 'pointer'
                        }}
                        title={t('admin.moveUp')}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => handleReorderWord(idx, 'down')}
                        disabled={idx === arr.length - 1}
                        style={{
                          ...styles.smallButton,
                          opacity: idx === arr.length - 1 ? 0.3 : 1,
                          cursor: idx === arr.length - 1 ? 'not-allowed' : 'pointer'
                        }}
                        title={t('admin.moveDown')}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ margin: 0, color: '#999' }}>{t('admin.wordsPlaceholder')}</p>
          )}
        </div>
        )}
        <input
          type="text"
          placeholder={unlockMode === 'keyword' ? t('admin.locationHint') : t('admin.locationHintProximity')}
          value={finalHint}
          onChange={(e) => setFinalHint(e.target.value)}
          style={styles.input}
        />
        {unlockMode === 'keyword' && (
          <p style={{ color: '#666', fontSize: '14px' }}>
            {t('admin.wordCount', { count: finalWords.trim().split(/\s+/).filter(w => w.length > 0).length, total: treasures.length })}
          </p>
        )}

        {unlockMode === 'keyword' && finalWords.trim().split(/\s+/).filter(w => w.length > 0).length === treasures.length && treasures.length > 0 && (
          <div style={{
            marginTop: '15px',
            padding: '12px',
            background: '#e8f4f8',
            borderRadius: '4px',
            borderLeft: '4px solid #007bff'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
              <strong>{t('admin.preview')}</strong>
            </p>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
              {finalWords.trim()}
            </p>
          </div>
        )}
          </section>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.button,
              background: '#28a745',
              fontSize: '18px',
              padding: '12px 20px',
              width: '100%',
              opacity: saving ? 0.6 : 1,
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? t('admin.saving') : t('admin.saveButton')}
          </button>
        </div>

        {/* Right Column: Map */}
        <div style={styles.rightColumn}>
          <AdminMap treasures={treasures} newTreasure={newTreasure} onSetCoords={handleSetCoords} />
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f5f5f5',
    padding: '20px',
    overflow: 'hidden'
  },
  title: {
    marginTop: 0,
    marginBottom: '10px'
  },
  mainLayout: {
    display: 'flex',
    flex: 1,
    gap: '20px',
    minHeight: 0,
    overflow: 'hidden'
  },
  leftColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto'
  },
  rightColumn: {
    width: '500px',
    minWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto'
  },
  section: {
    marginBottom: '20px',
    padding: '20px',
    background: '#f9f9f9',
    borderRadius: '8px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  },
  button: {
    padding: '10px 16px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  smallButton: {
    padding: '4px 8px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  treasuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '10px'
  },
  treasureItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    background: 'white',
    borderRadius: '4px',
    border: '1px solid #ddd'
  },
  message: {
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px',
    fontSize: '14px'
  }
}
